"use client"

import { useEffect, useState } from "react"

interface FunnelOwnerResult {
  funnelOwnerId?: number
  loading: boolean
  error?: string
}

/**
 * Determina el dueño (owner_id / user_id) del embudo actualmente renderizado.
 * Soporta:
 *  - /funnel-view/[pageId]
 *  - /funnel-preview/[funnelId]/[pageId]
 *
 * Nota: Resuelve por page_id para ambos casos (más estable).
 */
export function useFunnelOwnerId(): FunnelOwnerResult {
  const [funnelOwnerId, setFunnelOwnerId] = useState<number>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()

  useEffect(() => {
    let cancelled = false

    async function fetchOwner() {
      try {
        const path = window.location.pathname

        // ✅ match /funnel-view/274
        const mView = path.match(/^\/funnel-view\/(\d+)(?:\/)?$/)

        // ✅ match /funnel-preview/671/274
        const mPreview = path.match(/^\/funnel-preview\/(\d+)\/(\d+)(?:\/)?$/)

        let pageId: number | null = null

        if (mView) {
          pageId = Number(mView[1])
        } else if (mPreview) {
          pageId = Number(mPreview[2]) // el segundo grupo es pageId
        }

        if (!pageId || !Number.isFinite(pageId)) {
          if (!cancelled) {
            setError("No pageId found in URL")
            setLoading(false)
          }
          return
        }

        // ✅ Llamar al endpoint que resuelve owner por page_id
        const res = await fetch(`/api/funnels/owner?page_id=${pageId}`, {
          method: "GET",
          cache: "no-store",
          headers: { "Accept": "application/json" },
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const data = await res.json()

        const owner = data?.owner_id ?? data?.funnel_owner_id ?? data?.user_id
        if (owner != null && String(owner).trim() !== "") {
          if (!cancelled) setFunnelOwnerId(Number(owner))
        } else {
          if (!cancelled) setError("No owner_id found for funnel")
        }
      } catch (err: any) {
        console.error("Error resolving funnel owner:", err)
        if (!cancelled) setError(err?.message || "Unknown error")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchOwner()
    return () => {
      cancelled = true
    }
  }, [])

  return { funnelOwnerId, loading, error }
}